/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Inject, Injectable } from '@angular/core';

import {
  BuilderAuthNumberService,
  BuilderUserContext,
  CommonActions,
  ComponentItemFinderDialogConfig,
  componentItemFinderDialogRoute,
  ComponentItemFinderListItem,
  CostCenterFinderDialogConfig,
  costCenterFinderDialogRoute,
  CostCenterFinderListItem,
  FormActionPipelines,
  FormFlowState,
  HousePlanFinderDialogConfig,
  housePlanFinderDialogRoute,
  HousePlanFinderListItem,
  makeComponentItemFinderId,
} from '@gh/builder-shared';
import { ComponentItemFilterMode, PRIVILEGED_EP_PB_APPLY_DEFAULT_MARKUPS } from '@gh/builder-shared-data';
import {
  ActionFlow,
  AppRouter,
  ContainerState,
  FormStateSnapshot,
  getAllNewSelectedEntities,
  getFormStateSnapshotValue,
  hasFormStateError,
  PAGE_STATE,
  queryDirty,
  queryFormMode,
  State,
} from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import {
  DialogService,
  FormMode,
  isCreateFormMode,
  NotificationService,
  WaitingService,
  yesAnswer,
  yesNoConfirmation,
} from '@gh/core-ui';
import { concat$, of$ } from '@gh/rx';
import { filter$, map$, switchMap$ } from '@gh/rx/operators';

import { first, isEmpty, noop, sortBy } from 'lodash';
import { Observable } from 'rxjs';

import { PriceBookDetailsBusinessService } from '../../resources/price-book-details-business.service';
import {
  componentStructureFromFinder,
  getPbCcStructureId,
  getPbCostCenterId,
  getPbHousePlanId,
  getPbItemId,
  isCcNotPreloaded,
  PbCcStructure,
  PbCostCenter,
  PbDefaults,
  PbDetails,
  PbHousePlan,
  PbHousePlanFilter,
  pbHousePlanFromFinder,
  PbMasterInfo,
} from '../../resources/price-book.model';
import {
  hasPbDefaultsErrors,
  hasPbMasterInfoErrors,
  pbCostCenterFromFinder,
  PbDetailsState,
  PbDetailsStateCostCenterLoadEvent,
  PbDetailsStateEvent,
  PbDetailsStateEventType,
  PbDetailsTab,
  queryCostCenter,
  queryCostCenterStructures,
  queryExistingCostCenters,
  queryExistingHousePlans,
  queryGetCostCenterRequest,
  queryPbDefaults,
  queryPbHasErrors,
  queryPbMasterInfo,
  queryPriceBookId,
  querySaveModel,
  querySelectedCostCenterId,
} from './pb-details.state';

@Injectable()
export class PbCommandService {
  constructor(private state: State<PbDetailsState>,
              @Inject(PAGE_STATE) private form: ContainerState<PbDetailsState>,
              private userContext: BuilderUserContext,
              private dialogService: DialogService,
              private waitingService: WaitingService,
              private notificationService: NotificationService,
              private businessService: PriceBookDetailsBusinessService,
              private pipelines: FormActionPipelines,
              private commonActions: CommonActions,
              private authNumberService: BuilderAuthNumberService,
              private router: AppRouter) {

  }

  costCenterAdd(): void {
    this.dialogService.openWithInput(costCenterFinderDialogRoute, <CostCenterFinderDialogConfig>{
      multiselection: true,
      preselected: queryExistingCostCenters(this.state.value).map(getPbCostCenterId),
    }).afterClosed().pipe(
      filter$(Boolean),
      map$(getAllNewSelectedEntities))
      .subscribe((costCenters: CostCenterFinderListItem[]) => {
        this.state.mutate({
          type: PbDetailsStateEventType.CostCenterAddSet,
          costCenters: costCenters.map(pbCostCenterFromFinder),
        });
        const firstCc = first(sortBy(costCenters, 'code'));
        if (firstCc) {
          this.state.mutate({
            type: PbDetailsStateEventType.CostCenterSelect,
            costCenterId: firstCc.id,
          });
        }
      });
  }

  costCenterRemove(costCenter: PbCostCenter): void {
    this.dialogService
      .confirmation(yesNoConfirmation('msg_pb_cc_delete', { number: costCenter.code.toString() })).pipe(
      filter$(yesAnswer))
      .subscribe(() =>
        this.state.mutate({
          type: PbDetailsStateEventType.CostCenterRemove,
          costCenterId: getPbCostCenterId(costCenter),
        }));
  }

  costCenterSelect(costCenterId?: ID): void {
    concat$(
      this.ensureCcLoad(costCenterId),
      of$({ type: PbDetailsStateEventType.CostCenterSelect, costCenterId }),
    ).subscribe((event) => this.state.mutate(event));
  }

  load(details: PbDetails, mode: FormMode, sourcePriceBookId?: ID): void {
    this.state.mutate({ type: PbDetailsStateEventType.Load, details, mode, sourcePriceBookId });
  }

  selectTab(tab: PbDetailsTab): void {
    const isNeedLoad = tab === PbDetailsTab.ITEMS_AND_COMPONENTS
      && !this.state.project(querySelectedCostCenterId)
      && !isEmpty(this.state.project(queryExistingCostCenters));

    this.state.mutate({ type: PbDetailsStateEventType.SelectTab, tab });

    if (isNeedLoad) {
      // tslint:disable-next-line:no-non-null-assertion
      const costCenterId = getPbCostCenterId(first(this.state.project(queryExistingCostCenters))!);

      concat$(
        this.ensureCcLoad(costCenterId),
        of$({ type: PbDetailsStateEventType.CostCenterSelect, costCenterId }),
      ).subscribe((event) => this.state.mutate(event));
    }
  }

  setMasterInfo(masterInfo: FormStateSnapshot<PbMasterInfo>): void {
    this.state.mutate({ type: PbDetailsStateEventType.SetMasterInfo, masterInfo });
  }

  setDefaults(defaults: FormStateSnapshot<PbDefaults>): void {
    this.state.mutate({ type: PbDetailsStateEventType.SetDefaults, defaults });
  }

  setRootField(field: string, value: any): void {
    this.state.mutate({ type: PbDetailsStateEventType.SetRootField, field, value });
  }

  updateHpFilter(field: keyof PbHousePlanFilter, value: any): void {
    this.state.mutate({ type: PbDetailsStateEventType.UpdateHpFilter, field, value });
  }

  addHousePlans(): void {
    if (hasFormStateError(this.state.project(queryPbDefaults))) {
      this.notificationService.error('msg_pb_correct_default_data');
      return;
    }

    this.dialogService.openWithInput(housePlanFinderDialogRoute, <HousePlanFinderDialogConfig>{
      multiselection: true,
      disableShowSeSwitcher: true,
      disableShowQqSwitcher: true,
      sourceOfficeId: this.userContext.office!.id, // tslint:disable-line:no-non-null-assertion
      filter: { hideZeroFloorArea: true },
      preselected: this.state.project(queryExistingHousePlans).map(getPbHousePlanId),
    }).afterClosed().pipe(
      filter$(Boolean),
      map$(getAllNewSelectedEntities),
      map$((housePlans: HousePlanFinderListItem[]) =>
        housePlans.map((hp) => pbHousePlanFromFinder(hp,
                                                     getFormStateSnapshotValue(this.state.project(queryPbDefaults))))))
      .subscribe((housePlans) => this.state.mutate({ type: PbDetailsStateEventType.HousePlanAddSet, housePlans }));
  }

  updateHousePlan(housePlan: PbHousePlan): void {
    this.state.mutate({ type: PbDetailsStateEventType.HousePlanUpdate, housePlan });
  }

  removeHousePlan(hp: PbHousePlan): void {
    this.dialogService.confirmation(yesNoConfirmation('msg_pb_hp_delete', { code: hp.code })).pipe(
      filter$(yesAnswer))
      .subscribe(() => this.state.mutate({ type: PbDetailsStateEventType.HousePlanRemove, housePlan: hp }));
  }

  selectHousePlan(hp: PbHousePlan): void {
    this.state.mutate({ type: PbDetailsStateEventType.HousePlanSelect, housePlan: hp });
  }

  applyDefaultMarkups(): void {
    if (this.ifSaved()) {
      this.authNumberService.openAuthNumberRequest(PRIVILEGED_EP_PB_APPLY_DEFAULT_MARKUPS).pipe(
        switchMap$((privilegedToken) =>
          // tslint:disable-next-line:no-non-null-assertion
          this.businessService.applyDefaults(this.state.project(queryPriceBookId)!, privilegedToken).pipe(
            (this.waitingService.operator()))))
        .subscribe(
          (details) => this.state.mutate({ type: PbDetailsStateEventType.Load, details, mode: FormMode.EditMode }),
          this.notificationService.processAll);
    }

  }

  reprice(): void {
    if (this.ifSaved()) {
      this.dialogService.confirmation(yesNoConfirmation('msg_pb_reprice')).pipe(
        filter$(yesAnswer),
        switchMap$(() =>
          this.businessService.reprice(this.state.project(queryPriceBookId)!).pipe(// tslint:disable-line:no-non-null-assertion
            (this.waitingService.operator()))))
        .subscribe(
          (details) => this.state.mutate({ type: PbDetailsStateEventType.Load, details, mode: FormMode.EditMode }),
          this.notificationService.processAll);
    }
  }

  completeFlow(): ActionFlow<FormFlowState<PbDetailsState>> {
    return this.pipelines.complete({
      validateForm: this.commonActions.validateWithEffect(
        (fs: FormFlowState<PbDetailsState>) => this.state.project(queryPbHasErrors),
        () => this.handlePbErrors()),
      execute: this.commonActions.execute((state: FormFlowState<PbDetailsState>) =>
        <any>this.businessService.save(querySaveModel(state.value))),
      finish: (state: FormFlowState<any>) => {
        this.state.mutate({
          type: PbDetailsStateEventType.MergeOnSave,
          calculation: state.value,
        });

        if (this.state.project(queryFormMode) === FormMode.CreateMode) {
          this.router.switchTo(['/estimating/price-books', state.value.priceBookId]);
        }
      },
    });
  }

  recalculateFlow(): ActionFlow<FormFlowState<PbDetailsState>> {
    return this.pipelines.recalculate({
      validateForm: this.commonActions.validateWithEffect(
        (state: FormFlowState<PbDetailsState>) => this.state.project(queryPbHasErrors),
        () => this.handlePbErrors()),
      execute: this.commonActions.execute((state: FormFlowState<PbDetailsState>) =>
        <any>this.businessService.recalculate(this.state.project(querySaveModel))),
      finish: (state: FormFlowState<PbDetailsState>) => this.state.mutate({
        type: PbDetailsStateEventType.MergeOnCalculate,
        calculation: state.value,
      }),
    });
  }

  structureSetField(name: string, value: any, structureId?: ID): void {
    this.state.mutate({ type: PbDetailsStateEventType.StructureSetField, structureId, name, value });
  }

  structureAdd(): void {
    const costCenterId = this.state.project(querySelectedCostCenterId)!; // tslint:disable-line:no-non-null-assertion

    this.dialogService.openWithInput(componentItemFinderDialogRoute, <ComponentItemFinderDialogConfig>{
      filterMode: ComponentItemFilterMode.All,
      disableFilterMode: false,
      multiselection: true,
      preselected: this.state.project((s) => queryCostCenterStructures(s, costCenterId))
        .map((element) => makeComponentItemFinderId(getPbItemId(element), element.isComponent)),
    }).afterClosed().pipe(
      filter$(Boolean),
      map$(getAllNewSelectedEntities))
      .subscribe((structures: ComponentItemFinderListItem[]) => {
        this.state.mutate({
          type: PbDetailsStateEventType.StructureAdd,
          structures: structures.map((structure) => componentStructureFromFinder(
            structure,
            getFormStateSnapshotValue(this.state.project(queryPbDefaults)),
            costCenterId)), // tslint:disable-line:no-non-null-assertion
        });
      });
  }

  structureRemove(structure: PbCcStructure): void {
    this.dialogService
      .confirmation(yesNoConfirmation('msg_pb_ic_delete', { code: structure.code })).pipe(
      filter$(yesAnswer))
      .subscribe(() =>
        this.state.mutate({
          type: PbDetailsStateEventType.StructureRemove,
          structureId: getPbCcStructureId(structure),
        }));
  }

  structureSelect(structureId?: ID): void {
    this.state.mutate({ type: PbDetailsStateEventType.StructureSelect, structureId });
  }

  resetFocusedCell(): void {
    this.state.mutate({ type: PbDetailsStateEventType.ResetFocusedCell });
  }

  openLinkJst(): void {
    this.openLinks(['/estimating/price-books', this.state.project(queryPriceBookId), 'links', 'jst']);
  }

  openLinkCsm(): void {
    this.openLinks(['/estimating/price-books', this.state.project(queryPriceBookId), 'links', 'csm']);
  }

  private openLinks(commands: any[]): void {
    const formMode = this.state.project(queryFormMode);
    if (isCreateFormMode(formMode)) {
      this.notificationService.error('msg_save_first');
    } else if (this.state.project(queryDirty)) {
      this.dialogService.confirmation(yesNoConfirmation('msg_pb_unsaved_changes')).pipe(
        filter$(yesAnswer),
        switchMap$(() => {
          const flow = this.completeFlow();

          return flow.run({ trigger: 'process', value: this.state.value, form: this.form })
            .toFinalState().pipe(
              filter$((state) => !!state.success));
        }))
        .subscribe(() => {
          this.router.navigateToChild(commands);
        });
    } else {
      this.router.navigateToChild(commands);
    }
  }

  private ifSaved(): boolean {
    if (this.state.project(queryDirty)) {
      this.notificationService.error('msg_save_first');
      return false;
    }

    return true;
  }

  private ensureCcLoad(costCenterId?: ID): Observable<PbDetailsStateEvent> {
    return of$(costCenterId).pipe(
      filter$(Boolean),
      map$((id: any) => this.state.project((state) => queryCostCenter(state, id))),
      filter$(Boolean),
      filter$(isCcNotPreloaded),
      switchMap$((cc) =>
        // tslint:disable-next-line:no-non-null-assertion
        this.businessService.loadCostCenter(this.state.project((s) =>
          // tslint:disable-next-line:no-non-null-assertion
          queryGetCostCenterRequest(s, costCenterId!))).pipe((this.waitingService.operator()))),
      map$((structures) => (<PbDetailsStateCostCenterLoadEvent>{
        type: PbDetailsStateEventType.CostCenterLoad,
        costCenterId,
        structures,
      })));
  }

  private handlePbErrors(): void {
    if (!hasPbMasterInfoErrors(this.state.project(queryPbMasterInfo))
      && !hasPbDefaultsErrors(this.state.project(queryPbDefaults))) {
      this.notificationService.error('msg_he_incorrect_data_in_tables');
    }

    this.state.mutate({ type: PbDetailsStateEventType.NavigateToInvalid });
  }
}
